import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '../../hooks/useAuthPatched';
import axios from 'axios';
import { getApiUrl } from '../../config';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useForm } from '../../hooks/useForm';
import { formatPhoneNumber, formatZipCode } from '../../utils/enhancedValidation';
import { validators } from '../../utils/validation';
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    Typography,
    InputAdornment,
    Fade,
    Zoom,
    LinearProgress,
    IconButton,
    Alert,
    Collapse,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Home as HomeIcon,
    LocationCity as CityIcon,
    Map as StateIcon,
    Pin as ZipIcon,
    ArrowForward as ArrowForwardIcon,
    CheckCircle as CheckCircleIcon,
    Shield as ShieldIcon,
    Speed as SpeedIcon,
    TrendingUp as TrendingUpIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

// Keyframe animations
const float = keyframes`
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(5deg); }
`;

const pulse = keyframes`
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.05); }
`;

const shimmer = keyframes`
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

const gradientMove = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

// Styled components
const PageContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    background: 'linear-gradient(135deg, #0a0f1c 0%, #1a1f35 50%, #0d1225 100%)',
    position: 'relative',
    overflow: 'hidden',
}));

const AnimatedBackground = styled(Box)({
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    '& .orb': {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(80px)',
        animation: `${pulse} 8s ease-in-out infinite`,
    },
    '& .orb-1': {
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
        top: '-100px',
        left: '-100px',
    },
    '& .orb-2': {
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
        bottom: '-150px',
        right: '-150px',
        animationDelay: '2s',
    },
    '& .orb-3': {
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)',
        top: '40%',
        right: '20%',
        animationDelay: '4s',
    },
});

const GridPattern = styled(Box)({
    position: 'absolute',
    inset: 0,
    backgroundImage: `
        linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
    `,
    backgroundSize: '50px 50px',
    opacity: 0.5,
});

const LeftPanel = styled(Box)(({ theme }) => ({
    flex: '0 0 45%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(8),
    position: 'relative',
    zIndex: 1,
    [theme.breakpoints.down('lg')]: {
        flex: '0 0 40%',
        padding: theme.spacing(6),
    },
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
}));

const RightPanel = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    position: 'relative',
    zIndex: 1,
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(2),
    },
}));

const FormCard = styled(Box)(({ theme }) => ({
    width: '100%',
    maxWidth: '520px',
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: theme.spacing(5),
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
        borderRadius: '16px',
    },
}));

const BrandLogo = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '48px',
    '& .logo-icon': {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        fontWeight: 700,
        color: 'white',
    },
    '& .logo-text': {
        fontSize: '28px',
        fontWeight: 700,
        background: 'linear-gradient(135deg, #fff 0%, #94a3b8 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
});

const FeatureItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    '& .icon-wrapper': {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': {
            color: '#3b82f6',
            fontSize: '24px',
        },
    },
}));

const FloatingElement = styled(Box)(({ delay = 0 }) => ({
    position: 'absolute',
    animation: `${float} 6s ease-in-out infinite`,
    animationDelay: `${delay}s`,
    opacity: 0.6,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '12px',
        color: '#fff',
        transition: 'all 0.3s ease',
        '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s ease',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(59, 130, 246, 0.5)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#3b82f6',
            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
        },
        '&.Mui-error fieldset': {
            borderColor: '#ef4444',
        },
    },
    '& .MuiInputLabel-root': {
        color: 'rgba(255, 255, 255, 0.5)',
        '&.Mui-focused': {
            color: '#3b82f6',
        },
        '&.Mui-error': {
            color: '#ef4444',
        },
    },
    '& .MuiInputAdornment-root svg': {
        color: 'rgba(255, 255, 255, 0.4)',
    },
    '& .MuiFormHelperText-root': {
        color: 'rgba(255, 255, 255, 0.5)',
        '&.Mui-error': {
            color: '#ef4444',
        },
    },
}));

const GradientButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
    backgroundSize: '200% 200%',
    animation: `${gradientMove} 5s ease infinite`,
    borderRadius: '12px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    color: '#fff',
    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(59, 130, 246, 0.5)',
    },
    '&:disabled': {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'rgba(255, 255, 255, 0.3)',
        boxShadow: 'none',
    },
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
    height: '4px',
    borderRadius: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '& .MuiLinearProgress-bar': {
        background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
        borderRadius: '2px',
    },
}));

const StepIndicator = styled(Box)(({ active, completed }) => ({
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    ...(completed ? {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#fff',
    } : active ? {
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        color: '#fff',
        boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.2)',
    } : {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'rgba(255, 255, 255, 0.4)',
    }),
}));

const steps = [
    { id: 'personal', label: 'Personal Info', fields: ['firstName', 'lastName'] },
    { id: 'contact', label: 'Contact Details', fields: ['email', 'phone'] },
    { id: 'address', label: 'Address', fields: ['address', 'city', 'state', 'zip'] },
];

const features = [
    {
        icon: <SpeedIcon />,
        title: 'Lightning Fast Execution',
        description: 'Execute trades in milliseconds with our high-performance matching engine.',
    },
    {
        icon: <ShieldIcon />,
        title: 'Bank-Grade Security',
        description: 'Your assets are protected with enterprise-level security and cold storage.',
    },
    {
        icon: <TrendingUpIcon />,
        title: 'Advanced Trading Tools',
        description: 'Access professional charts, order types, and real-time market data.',
    },
];

const fieldConfig = {
    firstName: { label: 'First Name', icon: <PersonIcon />, placeholder: 'John', type: 'text' },
    lastName: { label: 'Last Name', icon: <PersonIcon />, placeholder: 'Doe', type: 'text' },
    email: { label: 'Email Address', icon: <EmailIcon />, placeholder: 'john@example.com', type: 'email', fullWidth: true },
    phone: { label: 'Phone Number', icon: <PhoneIcon />, placeholder: '(555) 123-4567', type: 'tel', fullWidth: true },
    address: { label: 'Street Address', icon: <HomeIcon />, placeholder: '123 Main Street', type: 'text', fullWidth: true },
    city: { label: 'City', icon: <CityIcon />, placeholder: 'New York', type: 'text' },
    state: { label: 'State', icon: <StateIcon />, placeholder: 'NY', type: 'text', maxLength: 2 },
    zip: { label: 'ZIP Code', icon: <ZipIcon />, placeholder: '10001', type: 'text' },
};

const ProfileCompletionPage = ({ missingFields = [], initialData = {}, onComplete }) => {
    const { getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();
    const { refreshProfile } = useUserProfile();
    const [currentStep, setCurrentStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Use all fields if none specified
    const fieldsToComplete = missingFields.length > 0
        ? missingFields
        : ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zip'];

    // Build validation rules
    const validationRules = {};
    fieldsToComplete.forEach(field => {
        const rules = [validators.required];
        if (field === 'firstName' || field === 'lastName') {
            rules.push(validators.minLength(2), validators.pattern(/^[a-zA-Z\s'-]+$/, 'Please enter a valid name'));
        } else if (field === 'email') {
            rules.push(validators.email);
        } else if (field === 'phone') {
            rules.push(validators.phone);
        } else if (field === 'address') {
            rules.push(validators.minLength(5));
        } else if (field === 'city') {
            rules.push(validators.minLength(2));
        } else if (field === 'state') {
            rules.push(validators.state);
        } else if (field === 'zip') {
            rules.push(validators.zipCode);
        }
        validationRules[field] = rules;
    });

    // Initialize form values
    const initialValues = {};
    fieldsToComplete.forEach(field => {
        initialValues[field] = initialData[field] || '';
    });

    const {
        values,
        errors,
        touched,
        handleChange: formHandleChange,
        handleBlur,
        handleSubmit,
    } = useForm(initialValues, validationRules);

    // Filter steps to only include those with missing fields
    const activeSteps = steps.filter(step =>
        step.fields.some(field => fieldsToComplete.includes(field))
    );

    // Calculate progress
    const filledFields = fieldsToComplete.filter(f => values[f] && values[f].trim()).length;
    const progress = (filledFields / fieldsToComplete.length) * 100;

    // Check if current step is complete
    const isCurrentStepComplete = () => {
        const currentFields = activeSteps[currentStep]?.fields.filter(f => fieldsToComplete.includes(f)) || [];
        return currentFields.every(f => values[f] && values[f].trim() && !errors[f]);
    };

    // Check if entire form is valid
    const isFormValid = fieldsToComplete.every(f => values[f] && values[f].trim() && !errors[f]);

    // Custom change handler with formatting
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'phone') {
            e.target.value = formatPhoneNumber(value);
        } else if (name === 'zip') {
            e.target.value = formatZipCode(value);
        } else if (name === 'state') {
            e.target.value = value.toUpperCase().slice(0, 2);
        }
        formHandleChange(e);
    };

    const handleNext = () => {
        if (currentStep < activeSteps.length - 1) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const onSubmit = async (formData) => {
        setSubmitting(true);
        setError(null);

        try {
            const token = await getAccessTokenSilently();
            const completeProfile = { ...initialData, ...formData };

            await axios.put(`${getApiUrl('javaBackend')}/api/user/profile`, completeProfile, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            await refreshProfile();
            setSuccess(true);

            setTimeout(() => {
                if (onComplete) {
                    onComplete(completeProfile);
                } else {
                    navigate('/trade');
                }
            }, 1500);
        } catch (err) {
            console.error('Error saving profile:', err);
            setError(err.response?.data?.message || 'Failed to save profile. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderField = (fieldName) => {
        const config = fieldConfig[fieldName];
        if (!config || !fieldsToComplete.includes(fieldName)) return null;

        return (
            <StyledTextField
                key={fieldName}
                fullWidth
                label={config.label}
                name={fieldName}
                type={config.type}
                value={values[fieldName] || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched[fieldName] && !!errors[fieldName]}
                helperText={touched[fieldName] && errors[fieldName]}
                placeholder={config.placeholder}
                disabled={submitting || success}
                inputProps={{ maxLength: config.maxLength }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            {config.icon}
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 2 }}
            />
        );
    };

    const currentStepFields = activeSteps[currentStep]?.fields.filter(f => fieldsToComplete.includes(f)) || [];

    if (success) {
        return (
            <PageContainer>
                <AnimatedBackground>
                    <div className="orb orb-1" />
                    <div className="orb orb-2" />
                </AnimatedBackground>
                <GridPattern />
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                    }}
                >
                    <Zoom in={success}>
                        <FormCard sx={{ textAlign: 'center' }}>
                            <CheckCircleIcon
                                sx={{
                                    fontSize: 80,
                                    color: '#10b981',
                                    mb: 3,
                                }}
                            />
                            <Typography
                                variant="h4"
                                sx={{
                                    color: '#fff',
                                    fontWeight: 700,
                                    mb: 2,
                                }}
                            >
                                Profile Complete!
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    mb: 3,
                                }}
                            >
                                Redirecting you to the trading terminal...
                            </Typography>
                            <CircularProgress
                                size={32}
                                sx={{ color: '#3b82f6' }}
                            />
                        </FormCard>
                    </Zoom>
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <AnimatedBackground>
                <div className="orb orb-1" />
                <div className="orb orb-2" />
                <div className="orb orb-3" />
            </AnimatedBackground>
            <GridPattern />

            {/* Floating decorative elements */}
            <FloatingElement sx={{ top: '15%', left: '8%' }} delay={0}>
                <Box
                    sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2))',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                />
            </FloatingElement>
            <FloatingElement sx={{ bottom: '20%', left: '15%' }} delay={2}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'rgba(6, 182, 212, 0.2)',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                    }}
                />
            </FloatingElement>

            {/* Left Panel - Features */}
            <LeftPanel>
                <BrandLogo>
                    <div className="logo-icon">R</div>
                    <span className="logo-text">Radix</span>
                </BrandLogo>

                <Typography
                    variant="h3"
                    sx={{
                        color: '#fff',
                        fontWeight: 700,
                        mb: 2,
                        lineHeight: 1.2,
                    }}
                >
                    Start Trading<br />
                    <span style={{
                        background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}>
                        Digital Assets
                    </span>
                </Typography>

                <Typography
                    sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '18px',
                        mb: 6,
                        maxWidth: 400,
                    }}
                >
                    Complete your profile to unlock access to our professional trading platform.
                </Typography>

                {features.map((feature, index) => (
                    <Fade in key={feature.title} style={{ transitionDelay: `${index * 150}ms` }}>
                        <FeatureItem>
                            <div className="icon-wrapper">
                                {feature.icon}
                            </div>
                            <Box>
                                <Typography
                                    sx={{
                                        color: '#fff',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        mb: 0.5,
                                    }}
                                >
                                    {feature.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.5)',
                                        fontSize: '14px',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {feature.description}
                                </Typography>
                            </Box>
                        </FeatureItem>
                    </Fade>
                ))}
            </LeftPanel>

            {/* Right Panel - Form */}
            <RightPanel>
                <FormCard component="form" onSubmit={handleSubmit(onSubmit)}>
                    {/* Progress indicator */}
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>
                                Profile Completion
                            </Typography>
                            <Typography sx={{ color: '#3b82f6', fontWeight: 600 }}>
                                {Math.round(progress)}%
                            </Typography>
                        </Box>
                        <ProgressBar variant="determinate" value={progress} />
                    </Box>

                    {/* Step indicators */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
                        {activeSteps.map((step, index) => {
                            const stepFields = step.fields.filter(f => fieldsToComplete.includes(f));
                            const isComplete = stepFields.every(f => values[f] && values[f].trim() && !errors[f]);
                            return (
                                <Box
                                    key={step.id}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <StepIndicator
                                        active={index === currentStep}
                                        completed={isComplete && index < currentStep}
                                        onClick={() => setCurrentStep(index)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {isComplete && index < currentStep ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : index + 1}
                                    </StepIndicator>
                                    {index < activeSteps.length - 1 && (
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 2,
                                                background: index < currentStep ? 'linear-gradient(90deg, #10b981, #059669)' : 'rgba(255, 255, 255, 0.1)',
                                                borderRadius: 1,
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Step title */}
                    <Typography
                        variant="h5"
                        sx={{
                            color: '#fff',
                            fontWeight: 600,
                            mb: 1,
                            textAlign: 'center',
                        }}
                    >
                        {activeSteps[currentStep]?.label}
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            mb: 4,
                            textAlign: 'center',
                        }}
                    >
                        {currentStep === 0 && "Let's start with your name"}
                        {currentStep === 1 && "How can we reach you?"}
                        {currentStep === 2 && "Where are you located?"}
                    </Typography>

                    {/* Error alert */}
                    <Collapse in={!!error}>
                        <Alert
                            severity="error"
                            onClose={() => setError(null)}
                            sx={{
                                mb: 3,
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                color: '#fca5a5',
                                '& .MuiAlert-icon': { color: '#ef4444' },
                            }}
                        >
                            {error}
                        </Alert>
                    </Collapse>

                    {/* Form fields */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                        {currentStepFields.map((fieldName, index) => {
                            const config = fieldConfig[fieldName];
                            const isFullWidth = config?.fullWidth ||
                                (fieldName === 'state' || fieldName === 'zip' ? false : currentStepFields.length === 1);

                            return (
                                <Box
                                    key={fieldName}
                                    sx={{
                                        flex: isFullWidth ? '1 1 100%' : '1 1 calc(50% - 8px)',
                                        minWidth: fieldName === 'state' ? '80px' : fieldName === 'zip' ? '120px' : '200px',
                                    }}
                                >
                                    {renderField(fieldName)}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Navigation buttons */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: currentStep > 0 ? 'space-between' : 'flex-end' }}>
                        {currentStep > 0 && (
                            <Button
                                variant="outlined"
                                onClick={handleBack}
                                disabled={submitting}
                                sx={{
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                    color: '#fff',
                                    borderRadius: '12px',
                                    px: 3,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    '&:hover': {
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                        background: 'rgba(255, 255, 255, 0.08)',
                                    },
                                }}
                            >
                                Back
                            </Button>
                        )}

                        {currentStep < activeSteps.length - 1 ? (
                            <GradientButton
                                onClick={handleNext}
                                disabled={!isCurrentStepComplete() || submitting}
                                endIcon={<ArrowForwardIcon />}
                            >
                                Continue
                            </GradientButton>
                        ) : (
                            <GradientButton
                                type="submit"
                                disabled={!isFormValid || submitting}
                                endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                            >
                                {submitting ? 'Saving...' : 'Complete Setup'}
                            </GradientButton>
                        )}
                    </Box>
                </FormCard>
            </RightPanel>
        </PageContainer>
    );
};

export default ProfileCompletionPage;
